/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Input,
  Button,
  CheckboxButtonGroup,
  CheckboxToggle,
  FileUpload,
  ButtonRequestRunInitiate,
  TextArea
} from '../'

import { normalizePhoneNumber, decodeBase64 } from '../../utils'

import {
  FILETYPE_CELLPHONE_NUMBER_CHANGE,
  REQUESTTYPE_CELLPHONE_NUMBER_CHANGE,
  NUMBER_CHANGE_ACTIVE
} from '../../constants'


class IdentityAddressAdd extends React.Component {
  static propTypes = {
    addressType: PropTypes.oneOf([ 'msisdn', 'landline', 'email', 'physical' ]).isRequired,
    identity: PropTypes.string.isRequired,
    onCancelClick: PropTypes.func.isRequired,
    onAdded: PropTypes.func.isRequired,
    onFailed: PropTypes.func.isRequired
  }

  state = {
    addressValue: '',
    optedOut: false,
    default: true,
    showOptInConfirmModal: false,
    uploadedFileId: null,
    fileUploadInProgress: false
  }

  saveAddress = () => {
    let input = {
      identity: this.props.identity,
      addressType: this.props.addressType,
      addressValue: this.props.addressType === 'msisdn'
        ? normalizePhoneNumber(this.state.addressValue)
        : this.state.addressValue,
      default: this.state.default,
      inactive: false,
      optedOut: this.state.optedOut
    }

    this.props.saveAddress({ variables: { input } })
      .then(({ data }) => {
        console.log('Address saved successfully')
        this.props.onAdded()
      })
      .catch((error) => {
        console.log('Saving address failed.', error)
        this.props.onFailed(error)
      })
  }

  getAddressType = () => (
    {
      msisdn: 'cellphone number',
      landline: 'landline number',
      email: 'email address',
      physical: 'physical address'
    }[this.props.addressType]
  )

  uploadSelectedFile = () => {
    this.setState({ fileUploadInProgress: true })

    let file = document.getElementById('file-input').files[0]
    let reader = new FileReader()
    let headers = new Headers({
      'Authorization': 'Token ' + localStorage.getItem('token') || null
    })

    let data = new FormData()
    data.append('file', file)
    data.append('filetype', FILETYPE_CELLPHONE_NUMBER_CHANGE)

    reader.onload = (e) => {
      fetch(`${process.env.REACT_APP_API_ROOT}/v1/file/`, {
        method: 'post',
        headers: headers,
        body: data
      })
      .then(response => {
        if (response.ok) {  // covers 200-range of http responses
          console.info("File uploaded")
          response.json()
            .then(data => (
              this.setState({
                uploadedFileId: decodeBase64(data.file),
                fileUploadInProgress: false
              })
            ))
        } else {
          console.error("File upload failed")
          this.setState({ fileUploadInProgress: false })
        }
      })
      .catch(error => {
        console.error(`File upload failed - ${error}`)
        this.setState({ fileUploadInProgress: false })
      })
    }

    reader.readAsText(file)
  }

  render() {
    return (
      <React.Fragment>
        <div className='slds-border_bottom slds-p-around_medium slds-grid'>
          {this.props.addressType === 'physical'
            ? <TextArea
                placeholder='Enter address'
                value={this.state.addressValue}
                rows={5}
                onChange={(e) => this.setState({ addressValue: e.target.value })}
                className='slds-size_1-of-3'
                autoFocus={true}
              />
            : <Input
                placeholder={this.props.addressType === 'email'
                  ? 'Enter address'
                  : 'Enter number'
                }
                value={this.state.addressValue}
                onChange={(e) => this.setState({ addressValue: e.target.value })}
                className='slds-size_1-of-3'
                autoFocus={true}
              />
          }
        </div>
        {NUMBER_CHANGE_ACTIVE
          ? <div className='slds-size_1-of-1 slds-grid slds-border_bottom slds-p-around_medium' style={{ alignItems: 'center' }}>
              Attach supporting document <i className='slds-p-left_xx-small'>(optional)</i>
              <FileUpload
                endpoint='/v1/file/'
                onFileSelect={this.uploadSelectedFile}
                filetype={null}
                fileRelationships={[]}
                noUploadButton={true}
                sldsStyle={true}
                className='slds-col_bump-left'
                style={{ width: '268px' }}
              />
            </div>
          : <div className='slds-size_1-of-1 slds-grid slds-border_bottom slds-p-around_medium'>
              {`Primary ${this.getAddressType()}`}
              <CheckboxToggle
                id="primary"
                checked={this.state.default}
                onChange={() => this.setState(prevState => (
                  { default: !prevState.default }
                ))}
                className='slds-col_bump-left'
              />
            </div>
        }
        <div className='slds-size_1-of-1 slds-grid slds-border_bottom slds-p-around_medium' style={{ alignItems: 'center' }}>
          Communication consent
          <CheckboxButtonGroup
            id="consent"
            options={[
              'Opted IN',
              'Opted OUT',
              'None'
            ]}
            selectedOption={this.state.optedOut === null
              ? 'None'
              : this.state.optedOut ? 'Opted OUT' : 'Opted IN'
            }
            onChange={(e) => {
              let value = e.target.value
              if (value === 'Opted IN') {
                this.setState({ showOptInConfirmModal: true })
              } else if (value === 'Opted OUT') {
                this.setState({ optedOut: true })
              } else {
                this.setState({ optedOut: null })
              }
              this.setState({ optedOut: e.target.value === 'Opted OUT'
                ? true
                : e.target.value === 'Opted IN' ? false : null
              })
            }}
            className='slds-col_bump-left'
          />
        </div>
        <div className='slds-size_1-of-1 slds-p-around_medium slds-m-top_medium'>
          <Button
            minWidth={100}
            onClick={this.props.onCancelClick}>
            Cancel
          </Button>
          {NUMBER_CHANGE_ACTIVE
            ? this.props.addressType === 'msisdn'
              ? <ButtonRequestRunInitiate
                  requestTypeId={REQUESTTYPE_CELLPHONE_NUMBER_CHANGE}
                  identityId={this.props.identity}
                  buttonType='brand'
                  disabled={
                    this.state.addressValue.length === 0 ||
                    this.state.initiateInProgress ||
                    this.state.fileUploadInProgress
                  }
                  onInitiateTriggered={() => this.setState({ initiateInProgress: true })}
                  onInitiateDone={requestRunId => (
                    this.setState(
                      { initiateInProgress: false },
                      this.props.onAdded()
                    )
                  )}
                  requestContentCaptureOnInitiate={{
                    'new_number': this.state.addressValue,
                    'file': this.state.uploadedFileId
                  }}
                  origin={this.state.uploadedFileId
                    ? 'form'
                    : 'management'
                  }
                  autoInitiateWorkflowRun={true}
                  minWidth={100}>
                  {this.state.initiateInProgress
                    ? 'Initiating...'
                    : 'Initiate'
                  }
                </ButtonRequestRunInitiate>
              : <Button
                  type='brand'
                  className='slds-m-left_small'
                  minWidth={100}
                  onClick={this.saveAddress}>
                  Add
                </Button>
            : <Button
                type='brand'
                className='slds-m-left_small'
                minWidth={100}
                onClick={this.saveAddress}>
                Add
              </Button>
          }
        </div>
      </React.Fragment>
    )
  }
}

const saveAddress = gql`
  mutation saveAddress($input: AddressMutationInput!) {
    addressMutate(input: $input) {
      address {
        id
        identity {
          id
          client {
            id
            division {
              id
              employer {
                id
                identity {
                  id
                }
              }
            }
          }
        }
        addressType
        addressValue
        default
        inactive
        optedOut
      }
    }
  }
`

const withAddressMutate =
  graphql(saveAddress, {
    name: 'saveAddress'
  })

export default withAddressMutate(IdentityAddressAdd)



// WEBPACK FOOTER //
// ./src/components/identity-address-add/index.js